import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from './FieldHeader';
import FieldInput from './FieldInput';

import Tooltip from '../../misc/Tooltip';

import { calculateShrinkFactor, getAutoShrinkClampStyles, getFittingMaxLines, getLineHeightPixels } from '../../../lib/autoShrinkHelper';
import {
  measureAutoHeight, createMeasurementSurrogate, asText, AUTO_HEIGHT_RETRY_DELAY_MS, AUTO_HEIGHT_MAX_RETRIES,
} from '../../../lib/autoHeightHelper';
import { isEmptyValue } from '../../../lib/validation';
import { InputPlaceholderGrey } from '../../../constants/Colors';
import FormStylesOptions from '../../../constants/FormStylesOptions';
import FieldTypes from '../../../constants/FieldTypes';
import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldModes from '../../../constants/FieldModes';
import PageLayoutModes from '../../../constants/PageLayoutModes';

/**
 * The minimum height of the auto-shrink display and editor nodes, in border-box pixels. It came
 * from inputGroupWrapStyles, where an empty input group cell needs a floor to stay clickable.
 * Shared with handleExportMeasurement, which has to know that the final export pass draws it.
 */
const AUTO_SHRINK_MIN_HEIGHT = 25;

const StyledWrapper = styled.div`
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}

  /*
   * Always pinned to the drawn rectangle, the exporter's measuring pass included: a text field
   * is measured at its drawn size and reports only the room its text lacks (see
   * handleExportMeasurement), so nothing here ever needs to grow to its contents.
   */
  height: 100%;

  /*
   * Plain wrap, tight box, editable on screen: clip here instead of leaving it to Field.js.
   *
   * This wrapper fills exactly the same box as Field.js's overflow: hidden wrapper, so clipping
   * here hides nothing that was not hidden already. The difference is what the box means to the
   * browser. hidden is a scroll container: the centred line's glyphs overhang the tight box by a
   * pixel (see tightClipMargin in render), that pixel counted as scrollable overflow, and Chrome
   * scrolled the Field.js wrapper by it to reveal the editor on focus and the caret on every
   * keystroke, leaving the text 1px higher after blur. clip is not a scroll container and does
   * not pass the overhang upward, so there is nothing to scroll. Off in pdf mode and when
   * disabled, where nothing is focused or typed and the export must keep the whole glyph.
   */
  ${(props) => (props.$clipOverflow ? 'overflow: clip;' : '')}
`;

const inputGroupWrapStyles = (props) => `
  width: 100%;
  background-color: ${props.backgroundColor};
  font-size: ${props.fontSize}px !important;
  color: ${props.fontColor};
  font-style: ${props.fontStyle};
  font-weight: ${props.fontWeight};
  text-align: ${props.textAlign};
  text-transform: ${props.textTransform};
  text-decoration: ${props.textDecoration};
  border-radius: ${props.borderRadius}px;
  border: ${props.border};
  padding: ${props.padding}px;
  min-height: 25px;
  box-sizing: border-box;
`;

const StyledInputGroupDisplay = styled.div`
  ${(props) => inputGroupWrapStyles(props)}
  white-space: pre-line;
  overflow-wrap: break-word;
  cursor: pointer;

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

const StyledInputGroupTextarea = styled.textarea`
  ${(props) => inputGroupWrapStyles(props)}
  resize: none;
  overflow: hidden;
  transition: border 0.15s ease-in-out;

  &:focus, &:focus-visible {
    outline: unset;
    border: ${(props) => props.borderWidth}px solid ${(props) => props.primaryColor};
  }

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

const StyledInternalWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  /* Same rule as the wrapper above: always pinned */
  height: 100%;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TextField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    type: PropTypes.string,
    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    wrapperStyles: PropTypes.object,
    inputGroupWrapMode: PropTypes.bool,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,
    titleTextOverflow: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    //Used for calculating the height of the element during PDF export
    measure: PropTypes.bool,
    onMeasureCompleted: PropTypes.func,
    measuredHeight: PropTypes.number,
    measuredOn: PropTypes.number,
    updatedOn: PropTypes.number,
    displayType: PropTypes.string,

    /**
     * The document's own auto-height setting, resolved by Field.js before it switches auto-height
     * off for grid pages in pdf mode. Two uses: it tells a plain wrapping field from an auto-height
     * one (see resolveIsPlainWrap), so the same field takes the same path in the builder and in a
     * PDF; and in a grid export it makes the field wrap and be measured even when its textOverflow
     * is ellipsis, as auto-height already does on screen (see getExporterFlags and isValueWrapping).
     */
    autoHeightRequested: PropTypes.bool,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),

    /**
     * Opt-in, used by input-group formula cells only: the text shown while NOT focused when it
     * differs from `value`. The editor still seeds from `value`. When undefined (every standalone
     * field, chart form and settings form) nothing below changes.
     */
    displayValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;

    /**
     * wrapperRef holds the outer element once the browser has drawn it, which is the only
     * way to ask how tall the field actually ended up. It lives in state rather than on a
     * plain property because storing it has to trigger another render: componentDidUpdate is
     * where the height is read, and it does not run again on its own.
     */
    /**
     * textBoxHeight is the room the display node really leaves for text, read off the drawn
     * element rather than worked out from the designed height. Working it out means guessing
     * at the title row, the wrapper margin and the grid item's own border and padding, and
     * those guesses came out a couple of pixels short, which is enough to clip the descenders
     * off the last line.
     */
    this.state = { focused: false, changes: value, wrapperRef: null, textBoxHeight: null };
    this.textareaRef = createRef();
    this.wrapDisplayHeight = null;

    //The idle display node (null while the editor is mounted) and, for plain wrap, its text span
    this.displayNode = null;
    this.displayTextNode = null;

    //Auto-height and plain wrap: the display node being watched for size changes, and its observer
    this.observedDisplayNode = null;
    this.displayResizeObserver = null;

    /**
     * Auto-height only. One request per blur: { generation, value, edited }. The generation
     * ties every deferred emission and retry to the blur that made it; the value is what was
     * blurred; edited is the permission to shrink. See openAutoHeightRequest.
     */
    this.autoHeightRequest = null;
    this.autoHeightGeneration = 0;
    this.autoHeightTimer = null;
    this.autoHeightRetryTimer = null;
    this.autoHeightRetries = 0;
  }

  /**
   * Record the blur as a request. A blur is edited when the value it carries differs from the
   * committed one. A request already holding exactly this value is reused (the browser can fire
   * two native blurs for one loss of focus, and the second may already see the committed
   * value), keeping its generation and any permission it had. A different value opens a new
   * request and never inherits an old permission.
   *
   * @param {*} next //The value being blurred
   * @param {*} value //The committed value prop
   * @returns {Object}
   * @private
   */
  openAutoHeightRequest = (next, value) => {

    const edited = asText(next) !== asText(value);

    const current = this.autoHeightRequest;

    if (current && asText(current.value) === asText(next)) {
      current.edited = current.edited || edited;
      return current;
    }

    this.autoHeightGeneration += 1;

    this.autoHeightRequest = { generation: this.autoHeightGeneration, value: next, edited };

    return this.autoHeightRequest;

  };

  /**
   * Everywhere a request dies: a diverging value from outside, the field disabled, auto-height
   * switched off, the handler gone, unmount. Both timers go with it, the zero-delay emission
   * included, so nothing measured for the old request can reach the page.
   *
   * @private
   */
  cancelAutoHeightRequest = () => {
    clearTimeout(this.autoHeightTimer);
    clearTimeout(this.autoHeightRetryTimer);
    this.autoHeightTimer = null;
    this.autoHeightRetryTimer = null;
    this.autoHeightRequest = null;
    this.autoHeightRetries = 0;
  };

  /**
   * Send one measurement and act on the page's answer.
   *
   * 'applied' and 'settled' spend the request. 'retry' means the box was still animating, or
   * the document moved under the measurement: measure again once react-grid-layout's transition
   * has finished, from a surrogate of the editor (retryAutoHeight). Three attempts, backing
   * off, then the request is left for the next blur to reuse or replace.
   *
   * @param {Object} measurement //From measureAutoHeight, stamped with the request's generation
   * @private
   */
  emitAutoHeight = (measurement) => {

    const request = this.autoHeightRequest;

    //Cancelled between the blur and this timer, or replaced by a newer blur: never reaches the page
    if (!request || request.generation !== measurement.generation) return;

    const { onAutoHeightChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (!onAutoHeightChange) {
      this.cancelAutoHeightRequest();
      return;
    }

    const result = onAutoHeightChange(fieldId, fieldIdentifier, fieldPositionId, measurement);

    if (result !== 'retry') {
      if (this.autoHeightRequest === request) this.autoHeightRequest = null;
      this.autoHeightRetries = 0;
      return;
    }

    if (this.autoHeightRetries >= AUTO_HEIGHT_MAX_RETRIES) return;

    this.autoHeightRetries += 1;

    clearTimeout(this.autoHeightRetryTimer);

    //Backs off (250, 500, 1000ms): a throttled tab or a slow frame must not exhaust the attempts inside one animation
    const delay = AUTO_HEIGHT_RETRY_DELAY_MS * (2 ** (this.autoHeightRetries - 1));

    this.autoHeightRetryTimer = setTimeout(() => this.retryAutoHeight(request.generation), delay);

  };

  /**
   * Measure again after the box has settled. The editor is gone by now, so the text is read
   * from a hidden textarea that wraps like it (never the display: an auto-shrink display is
   * clamped to its line limit and drops a trailing newline), while the box facts come from the
   * pinned display node. The value is the request's own, not the prop: a controlled host may
   * not have committed it yet, and a foreign value cancels the request in componentDidUpdate.
   *
   * @param {Number} generation //The request this retry belongs to
   * @private
   */
  retryAutoHeight = (generation) => {

    const request = this.autoHeightRequest;

    if (!request || request.generation !== generation) return;

    const { type, height, disabled, autoHeightEnabled, enableValueWrap, fontSize } = this.props;
    const { focused } = this.state;

    const box = this.displayNode;

    //Focused again: the editor is up and its own blur will measure
    if (focused || disabled || !autoHeightEnabled || !enableValueWrap || !box) return;

    //The editor's own pitch: the line-height rule of the effective font, which the display already draws
    const fontPx = parseFloat(window.getComputedStyle(box).fontSize) || fontSize;
    const lineHeightPx = getLineHeightPixels(fontPx);

    const surrogate = createMeasurementSurrogate(box, { lineHeightPx, value: request.value });

    if (!surrogate) return;

    let measurement = null;

    try {
      measurement = measureAutoHeight(box, request.value, type, height, { edited: request.edited, contentNode: surrogate });
    } finally {
      surrogate.remove();
    }

    if (!measurement) return;

    measurement.generation = request.generation;

    this.emitAutoHeight(measurement);

  };

  moveCursorToEnd = () => {
    if (this.textareaRef.current) {
      const length = this.textareaRef.current.value.length;
      this.textareaRef.current.setSelectionRange(length, length);
    }
  };

  /**
   * Stores the auto-shrink editor element.
   *
   * This is a class property rather than an inline arrow function so its identity never changes.
   * An inline ref is a new function on every render, which makes React detach it (calling it with
   * null) and reattach it on every single render. Anything done inside such a ref therefore runs
   * on every keystroke.
   */
  setTextareaRef = (e) => {
    this.textareaRef.current = e;

    /**
     * Plain wrap only: focus the editor without letting the browser scroll anything to reveal it.
     *
     * Every other editor is focused by React's autoFocus, which calls a plain focus(). Chrome then
     * scrolls the nearest scrollable ancestor to bring the focused element's box into view, and
     * Field.js's overflow: hidden wrapper counts as scrollable. In a tight box the centred line's
     * glyphs overhang the box (see tightClipMargin in render), and that overhang makes the wrapper's
     * content 1px taller than the wrapper, so the first focus scrolled it by 1px and left it there:
     * the text jumped up on click and stayed up after blur. preventScroll leaves every ancestor
     * where it is. Nothing is lost: the display node this editor replaces has a tabIndex and was
     * already focused, and so already scrolled into view, before the editor mounted.
     *
     * Runs once, on mount, because this ref is a stable class property.
     */
    if (e && this.resolveIsPlainWrap()) e.focus({ preventScroll: true });
  };

  /**
   * Stores the display's text span, which the exporter measures for a plain wrapping field. A
   * class property for the same reason as setTextareaRef: an inline ref would be detached and
   * reattached on every render.
   */
  setDisplayTextRef = (e) => {
    this.displayTextNode = e;
  };

  /**
   * Is this a plain wrapping field: it wraps, and neither auto-shrink nor auto-height was asked for?
   *
   * <----------------------------NOTICE------------------------->
   * rc26 swapped the one line <input> for a wrapping div on every text field set to wrap, not
   * only on auto-shrink and auto-height ones. That div carries a 25px floor and vertical
   * padding, so the short boxes PDF forms are full of grew scrollbars on screen and doubled in
   * height in the export. Everything that undoes that is keyed on this one answer, and both
   * render and getExporterFlags ask it here, so what is drawn and what is measured can never
   * disagree.
   *
   * autoHeightRequested rather than autoHeightEnabled, because Field.js switches auto-height
   * off for grid pages in pdf mode. Keyed to the enabled flag, an auto-height field would take
   * the plain path in the PDF but not in the builder.
   *
   * enableValueWrap keeps number, uniqueId, chart and input-group usages of this component out.
   *
   * @private
   */
  resolveIsPlainWrap = () => {

    const { enableValueWrap, autoShrinkEnabled, autoHeightRequested, textOverflow } = this.props;

    return !!enableValueWrap
      && textOverflow !== FormStylesOptions.textOverflowEllipsis
      && !autoShrinkEnabled
      && !autoHeightRequested;

  };

  /**
   * Auto-height and plain wrap. Keep textBoxHeight in step with the display node's real size.
   *
   * <----------------------------NOTICE------------------------->
   * textBoxHeight is normally read in the display node's ref callback, which runs on every
   * render. That is enough while the box is static. When auto-height persists a new height,
   * react-grid-layout animates the item to it over 200ms, so the render that follows the
   * change measures a box that is still mostly its old size, and nothing renders again once
   * the animation settles. The line clamp then stays capped at the lines the OLD box could
   * hold, and the text the field just grew to show is cut off with an ellipsis until something
   * unrelated re-renders the field. Watching the node itself closes that gap. A plain wrapping
   * field has the same problem in the other direction: its tight-box rule (see render) is
   * decided from this number, and a resize read mid-animation would leave it wrong.
   *
   * Attached and detached from syncDisplayObserver only, never from a ref callback or render.
   * Screen only: syncDisplayObserver never attaches one in pdf mode, where nothing resizes.
   *
   * @private
   */
  observeDisplayNode = (node) => {

    if (typeof ResizeObserver === 'undefined') return;

    if (node === this.observedDisplayNode) return;

    if (this.displayResizeObserver) this.displayResizeObserver.disconnect();

    this.observedDisplayNode = node;

    this.displayResizeObserver = new ResizeObserver(() => {
      const { textBoxHeight } = this.state;

      //Read live rather than captured when the observer was attached, so a padding edit
      //followed by a resize can not compute the room with the old padding.
      const { padding } = this.props;

      const nextTextBoxHeight = node.clientHeight - ((padding || 0) * 2);
      if (nextTextBoxHeight !== textBoxHeight) this.setState({ textBoxHeight: nextTextBoxHeight });
    });

    this.displayResizeObserver.observe(node);

  };

  /**
   * Stops watching the display node and forgets it. Safe to call when nothing is being watched.
   *
   * @private
   */
  disconnectDisplayObserver = () => {
    if (this.displayResizeObserver) this.displayResizeObserver.disconnect();
    this.displayResizeObserver = null;
    this.observedDisplayNode = null;
  };

  /**
   * Attach or detach the box watcher according to the current render.
   *
   * Called from componentDidMount and componentDidUpdate only. It used to be called from the
   * display node's inline ref, which React detaches (null) and reattaches on every render, so a
   * "disconnect on null" there would churn; and nothing ever detached it when a field stopped
   * being eligible, so a field switched from auto-height to auto-shrink kept its watcher. Now
   * eligibility is re-checked after every commit: watch while the field is auto-height or plain
   * wrap, on screen, with the display node mounted (it is replaced by the editor while typing);
   * otherwise stop. observeDisplayNode returns early for the node it already watches, so an
   * ordinary re-render costs nothing.
   *
   * @private
   */
  syncDisplayObserver = () => {

    const { autoHeightEnabled, enableValueWrap, fieldMode } = this.props;

    const isAutoHeight = !!autoHeightEnabled && !!enableValueWrap;

    const eligible = (isAutoHeight || this.resolveIsPlainWrap())
      && fieldMode !== FieldModes.pdf
      && !!this.displayNode;

    if (eligible) this.observeDisplayNode(this.displayNode);
    else this.disconnectDisplayObserver();

  };

  componentDidMount() {
    const { focused } = this.state;
    if (focused) this.moveCursorToEnd();
    this.syncDisplayObserver();
  }

  componentDidUpdate(prevProps, prevState) {
    const { changes, focused } = this.state;
    const { value, disabled, autoHeightEnabled } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });

    /**
     * A request outlives the blur that made it (deferred emission, retries), so anything that
     * makes its measurement meaningless cancels it: a value from outside that is not the one
     * blurred (the host committing that same value is fine), the field disabled, or auto-height
     * switched off. Runs before the zero-delay emission fires, so a synchronous update lands
     * here first.
     */
    const request = this.autoHeightRequest;
    if (request) {
      const valueDiverged = value !== prevProps.value && asText(value) !== asText(request.value);
      const switchedOff = (disabled && !prevProps.disabled) || (!autoHeightEnabled && prevProps.autoHeightEnabled);
      if (valueDiverged || switchedOff) this.cancelAutoHeightRequest();
    }

    /**
     * Place the caret at the end once, on the render where the field becomes editable.
     *
     * This used to live in the editor's ref, which meant it ran on every render and dragged the
     * caret back to the end after every keystroke, so nothing except the tail of the value could
     * be edited. componentDidUpdate runs after the DOM is committed, so the element exists by
     * now, and moveCursorToEnd already null checks it.
     */
    if (!prevState.focused && focused) this.moveCursorToEnd();

    this.syncDisplayObserver();

    this.handleExportMeasurement();

  }

  /**
   * Report how tall this field really is, so the exporter can grow the box to fit.
   *
   * Runs from componentDidUpdate because the browser cannot answer "how tall is this?" until
   * it has actually drawn the element. The shape of this deliberately mirrors
   * TextareaField.componentDidUpdate, so a future change to the measuring rules is visible
   * in both files rather than only one.
   *
   * @private
   */
  handleExportMeasurement = () => {

    const {
      onMeasureCompleted, measuredHeight, measuredOn, updatedOn,
      fieldId, fieldIdentifier, fieldPositionId,
      value, type, height, fontSize, padding, borderWidth, autoHeightRequested,
    } = this.props;

    const { wrapperRef } = this.state;

    const { isMeasurementPass, isAutoHeightMeasurementPass, isPlainWrap } = this.getExporterFlags();

    //An empty box has no words to make room for
    const hasPopulatedValue = !isEmptyValue(value, FieldTypes.text);

    /**
     * Either we have never measured this field, or it was edited after the last measurement
     * was taken and that number is now stale. Both values are millisecond timestamps.
     */
    const remeasureRequired = !measuredHeight || (measuredOn && updatedOn && measuredOn < updatedOn);

    /**
     * Vanilla JS reports a height of 0 before the first paint, so the height is checked for
     * a real value rather than assumed to be usable.
     */
    const refHeightAvailable = wrapperRef && wrapperRef.getBoundingClientRect() && wrapperRef.getBoundingClientRect().height;

    if (wrapperRef && isAutoHeightMeasurementPass && hasPopulatedValue && refHeightAvailable && remeasureRequired) {

      /**
       * Auto-height on a float page: the box is kept and the overflow is reported, the same two
       * numbers a blur records. hasPopulatedValue above is the same test the exporter's float
       * whitelist applies, so the set of fields that report is the set the page waits for.
       *
       * Measured inside the frame callback, not before it. This display node is styled entirely
       * from props, so it is ready now, but TextareaField's fillable node derives its line
       * pitch from a ref one render later; both fields read at the same moment so the rule is
       * one rule. The emission was already deferred here, for the reason the branch below gives.
       */
      requestAnimationFrame(() => {

        //Same reader the builder uses on blur, pointed at the display node instead of the editor
        const measurement = measureAutoHeight(this.displayNode, value, type, height);

        if (!measurement) return;

        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {

          //Today's reading (scrollHeight less clientHeight): the exporter grows only, on the same pixel as before
          autoHeightOverflowPx: measurement.legacyOverflowPx,
          autoHeightBoxPx: measurement.boxHeightPx,
          measuredOn: new Date().getTime(),
        });

      });

      return;

    }

    if (wrapperRef && isMeasurementPass && hasPopulatedValue && refHeightAvailable && remeasureRequired) {

      /**
       * A wrapping text field on a grid page: plain, auto-shrink, auto-height or both. It stays
       * at the size the designer drew while it is measured (render keeps every node pinned and,
       * for a plain field, free of tight-box styling) and reports only the height its text needs
       * beyond that. ExportFormPage turns that into whole rows added to the drawn ones, and 0
       * keeps the box. Measuring the natural height instead, as this used to, counted the title
       * row, the padding, the border and the 25px floor, and turned every 20px field into a
       * 40px one and every 40px auto-shrink field into a 48px one.
       *
       * Everything is in border-box pixels: the room is the slot the node sits in (see below) and
       * the need is text plus vertical padding plus vertical border, so the two compare directly.
       * offsetHeight and clientHeight ignore the exporter's page transform, as they must.
       *
       * Plain fields: a value that fits on one line reports 0 whatever the room, because a
       * tight plain box centres and clips its one line on purpose, the way rc25's <input> did.
       * Every other field prints at full size, so even a one line value grows a box that cannot
       * hold it. Their need is floored at AUTO_SHRINK_MIN_HEIGHT because the final pass draws
       * that floor again (it is off only while measuring, see $measuring), and a box grown to
       * less than it would have the node overhang.
       *
       * Reported more than once if the field renders again during the pass, which any measured
       * field may do. The node is pinned, so every report carries the same number, and the page
       * overwrites by field id. Deferred to the next frame, as every measuring field is, so a
       * parent's state is not updated while React is still committing this render.
       */
      requestAnimationFrame(() => {

        const node = this.displayNode;
        const textNode = this.displayTextNode;

        if (!node || !textNode) return;

        /**
         * The slot the node was given: its parent's inner height less everything else in that
         * parent (the title row, and a tooltip when there is one). Not the node's own offsetHeight:
         * a border-box element can never be shorter than its padding plus border (12px with 5px of
         * padding), so in a slot shorter than that the node overhangs and reports the floor instead
         * of the slot, and the box would be grown too little (a 20px field holding a wrapped value
         * came out 4px short). The 25px floor is off in this pass, so nothing else inflates it.
         */
        const internal = node.parentElement;
        const siblingsHeight = internal
          ? [...internal.children].reduce((sum, child) => (child === node ? sum : sum + child.offsetHeight), 0)
          : 0;
        const room = internal ? Math.max(0, internal.clientHeight - siblingsHeight) : node.offsetHeight;

        //Every wrapped line at the normal pitch
        const textHeight = textNode.offsetHeight;

        const lineHeightPixels = getLineHeightPixels(fontSize);

        //+1 absorbs sub-pixel rounding; a second line would add a whole line height
        const fitsOneLine = isPlainWrap && textHeight <= lineHeightPixels + 1;

        /**
         * The floor is auto-shrink's alone. An auto-height field never draws it, in the builder
         * ($fitBox) or in a grid export ($noFloor), so flooring its need here would print a
         * one-line 32px box as 40px.
         */
        let needed = textHeight + ((padding || 0) * 2) + ((borderWidth || 0) * 2);
        if (!isPlainWrap && !autoHeightRequested) needed = Math.max(needed, AUTO_SHRINK_MIN_HEIGHT);

        const textOverflowPx = fitsOneLine ? 0 : Math.max(0, needed - room);

        onMeasureCompleted(fieldId, fieldIdentifier, fieldPositionId, {
          textOverflowPx,
          measuredOn: new Date().getTime(),
        });

      });

    }

  };

  /**
   * The questions the exporter needs answered about this field, resolved in one place so
   * componentDidUpdate and render can never disagree about them.
   *
   * The exporter has two ways of making room for a value, decided by the page layout, and a
   * field is in at most one of them.
   *
   * isExporterExpandable: "the exporter should let this field show everything it has."
   *   Grid pages only. True during BOTH exporter passes. JoyDocExporter renders the whole form
   *   twice, once invisibly to collect heights and once for real, and passes `measure` to
   *   both. This is the flag that turns off the font shrinking and the line clamp, because the
   *   finished document is produced by the second pass and it has to show every word at full
   *   size.
   *
   * isMeasurementPass: "we are in the invisible measuring pass right now."
   *   The only thing that separates the two passes is that the first is given somewhere to
   *   send its answer. GridLayout supplies onMeasureCompleted; JoyDocExporter's FieldRenderer
   *   deliberately does not. Nothing is unpinned in either pass: the field is measured at its
   *   drawn size and reports only the room its text lacks (handleExportMeasurement). What this
   *   flag does switch is the 25px floor on the auto-shrink nodes ($measuring): off while
   *   measuring, so the room read is the drawn box, and back on for the final render.
   *
   * isExporterAutoHeight: "the exporter grows this field the way the screen does."
   *   Float pages only. The field keeps its drawn box and renders exactly as it does on
   *   screen, shrunk font included, then reports how much of the value does not fit. That is
   *   the same number it reports on blur, and the exporter grows the box with the same
   *   calculator the builder uses. Every other field on a float page is left exactly as
   *   drawn, which is why expand is keyed to the grid layout and not to "not a fillable
   *   form": a float page mounted for one auto-height field must not let its neighbours grow.
   *
   * isAutoHeightMeasurementPass: the measuring pass of that mode, by the same test as above.
   *
   * @private
   */
  getExporterFlags = () => {

    const {
      measure, onMeasureCompleted, fieldMode, pageLayoutMode,
      displayType, enableValueWrap, autoShrinkEnabled, autoHeightEnabled, autoHeightRequested, textOverflow,
    } = this.props;

    /**
     * Does this field wrap at all? Same expression as isValueWrapping in render. The auto-height
     * term uses autoHeightRequested, not autoHeightEnabled: Field.js switches the latter off for
     * grid pages in pdf mode, but an auto-height field wraps on screen whatever its textOverflow,
     * and the export has to draw and measure that same node, or an auto-height field set to
     * ellipsis would print truncated. Mirrored by getExporterMeasurableFields.
     *
     * enableValueWrap is the guard that matters here. Field.js sets it for standalone text
     * fields only, so the number, uniqueId, chart axis and input group row usages of this
     * component can never reach any of the measuring behaviour below.
     */
    const isValueWrapping = (!!autoShrinkEnabled && !!enableValueWrap)
      || (!!autoHeightRequested && !!enableValueWrap)
      || (!!enableValueWrap && textOverflow !== FormStylesOptions.textOverflowEllipsis);

    /**
     * autoHeightEnabled already carries the type gate and, in pdf mode, the float page gate
     * (Field.js). enableValueWrap keeps number and uniqueId out, as it does for auto-shrink.
     * Together with the populated-value check in handleExportMeasurement this matches the
     * float branch of getExporterMeasurableFields exactly.
     */
    const isExporterAutoHeight = !!measure
      && fieldMode === FieldModes.pdf
      && pageLayoutMode === PageLayoutModes.float
      && displayType === FieldDisplayTypes.original
      && !!autoHeightEnabled && !!enableValueWrap;

    /**
     * These conditions match the grid branch of getExporterMeasurableFields exactly. If the
     * two drifted apart, a field could either render as though it had grown when it never
     * did, or be refused a measurement it was counted on to provide, which stalls the
     * exporter.
     *
     * "Not float" rather than "grid": a page saved without a layout property is a grid page,
     * as the on-screen renderer treats it, and such documents keep exporting as before.
     */
    const isExporterExpandable = !!measure
      && fieldMode === FieldModes.pdf
      && pageLayoutMode !== PageLayoutModes.float
      && displayType === FieldDisplayTypes.original
      && isValueWrapping;

    return {
      isExporterExpandable,
      isMeasurementPass: isExporterExpandable && !!onMeasureCompleted,

      //The same answer render uses; see resolveIsPlainWrap
      isPlainWrap: this.resolveIsPlainWrap(),

      isExporterAutoHeight,
      isAutoHeightMeasurementPass: isExporterAutoHeight && !!onMeasureCompleted,
    };

  };

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    if (this.displayResizeObserver) this.displayResizeObserver.disconnect();
    this.cancelAutoHeightRequest();

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {
      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);
      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes, wrapperRef, textBoxHeight } = this.state;

    const {

      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      type,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,
      titleTextOverflow,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      onDecoratorClick,
      placeholder,
      fieldMode,
      pageLayoutMode,
      fieldPresentationMode,
      decorators,
      inputGroupWrapMode,

      //Opt-in blurred text for input-group formula cells (see propTypes); renamed to avoid the local displayValue below
      displayValue: blurredDisplayValue,

    } = this.props;

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    const border = `${borderWidth}px solid ${borderColor}`;
    const displayValue = getDisplayValue(type, changes);
    const hasDisplayValue = blurredDisplayValue !== undefined;
    const blurredValue = hasDisplayValue ? getDisplayValue(type, blurredDisplayValue) : displayValue;
    const isWrapMode = inputGroupWrapMode && textOverflow !== 'ellipsis';

    /**
     * Only the props that are not already destructured above. title, titleDisplay,
     * titleFontSize, tabIndex, textOverflow and placeholder are all in scope already, and
     * redeclaring any of them here is a syntax error.
     */
    const {
      enableValueWrap,
      autoShrinkEnabled,
      autoShrinkMaxLines,
      autoShrinkMaxLength,
      autoHeightEnabled,
      autoHeightRequested,
      onAutoHeightChange,
      designWidth,
      designHeight,
      fontFamily,

      //The height the grid drew this field with, in the page's own unit; stamped on each measurement
      height,
    } = this.props;

    //Both flags originate in Field.js, which is the only place that can set them
    const isAutoShrinking = !!autoShrinkEnabled && !!enableValueWrap;

    /**
     * Auto-height, the opposite knob: the box grows to the value instead of the value being cut
     * to the box. Field.js has already checked the field type and, in pdf mode, only lets this
     * through on a float page, so a grid export never reaches any of the behaviour hanging off
     * it. enableValueWrap is checked here as well, so a number or uniqueId field carrying the
     * flag is still left alone.
     */
    const isAutoHeight = !!autoHeightEnabled && !!enableValueWrap;

    /**
     * Grid PDF export of an auto-height field. Field.js closes autoHeightEnabled there, so
     * $fitBox is off and the auto-shrink nodes would draw their 25px floor, which the builder
     * never draws for this field: an emptied one-line 32px box printed 40px. This switch drops
     * the floor in both export passes; handleExportMeasurement skips flooring the need for the
     * same fields. Auto-shrink-only fields keep the floor.
     */
    const isExportNoFloor = !!autoHeightRequested && !!enableValueWrap && fieldMode === FieldModes.pdf;

    /**
     * The wrap fix. A standalone text field wraps unless the ellipsis style was chosen
     * deliberately. Auto-shrink implies wrapping, so it turns this on regardless: where the
     * two disagree auto-shrink wins and textOverflow is ignored. Auto-height does the same, and
     * in pdf mode the document's own request is consulted because Field.js has switched
     * autoHeightEnabled off for grid pages there: the export must draw the node it measures
     * (see getExporterFlags), so an auto-height field set to ellipsis still wraps in a PDF.
     */
    const isValueWrapping = isAutoShrinking
      || isAutoHeight
      || (!!autoHeightRequested && !!enableValueWrap && fieldMode === FieldModes.pdf)
      || (!!enableValueWrap && textOverflow !== FormStylesOptions.textOverflowEllipsis);

    /**
     * See getExporterFlags for what these two mean and why they are not the same thing.
     * In short: the first is true whenever the exporter is drawing this field, the second
     * only while it is invisibly measuring it. Neither unpins anything: every node stays at
     * its drawn size in both passes and the measurement reports overflow only.
     */
    const { isExporterExpandable, isMeasurementPass, isExporterAutoHeight, isPlainWrap } = this.getExporterFlags();

    /**
     * Room taken by the field title above the input, so the shrink calculation does not
     * believe there is more vertical space than there is. The 4 is the header's gap under a
     * visible title (StyledFieldHeader in FieldHeader.js).
     */
    const titleHeight = (!title || titleDisplay === FormStylesOptions.displayNone)
      ? 0
      : Math.round((titleFontSize || 12) * 1.2) + 4;

    /**
     * <----------------------------NOTICE------------------------->
     * The shrink factor is calculated here, from `changes`, which is the live value as it is
     * being typed. It deliberately does not come from Field.js: this component keeps typed
     * text in local state until blur, so a parent side calculation would read the last saved
     * value and the font would not resize until the user clicked away.
     *
     * Skipped entirely when auto-shrink is off, so an ordinary field pays nothing for this.
     */
    const shrinkFactor = (!isAutoShrinking || isExporterExpandable || !designWidth || !designHeight) ? 1 : calculateShrinkFactor({
      text: changes,
      designWidth,
      designHeight,
      baseFontSize: fontSize,
      maxLines: autoShrinkMaxLines,
      maxLength: autoShrinkMaxLength,
      fontFamily,
      fontWeight,
      fontStyle,
      textTransform,
      padding,

      /**
       * The display node is box-sizing: border-box, so its border sits inside the drawn
       * height rather than outside it. Without this the calculation believes it has more
       * room than the browser will give it, and on a short field that gap is enough to let
       * the text spill past the border.
       */
      borderWidth,
      reservedHeight: titleHeight,

      //Exact once the node has been drawn. The three above are the first render fallback
      availableHeight: textBoxHeight,

      /**
       * With auto-height on the box is about to grow, so the height is an outcome rather than a
       * constraint and the font is decided by maxLines, maxLength and the width alone. Leaving
       * the height rule in would let the font creep back up after every growth and the field
       * would grow again on the next blur.
       */
      ignoreHeight: isAutoHeight,
    });

    //With auto-shrink off this is exactly the size the user set
    const effectiveFontSize = Math.max(1, Math.round(fontSize * shrinkFactor));

    //The line-height the CSS below will actually use, matching StyledAutoShrinkDisplayText
    const lineHeightPixels = getLineHeightPixels(effectiveFontSize);

    /**
     * Plain wrap only: the tight box.
     *
     * A box is tight when one line does not fit inside its vertical padding. The customer's
     * 20px fields leave 12px inside the border for a 17px line and 10px of padding. rc25's
     * <input> handled that by centring its one line over its own padding; the tight box does
     * the same (see StyledAutoShrinkDisplay). Never tight on the first render, before the node
     * has been measured, and never during the exporter's measuring pass, which has to read plain
     * geometry that no styling of its own can feed back into.
     *
     * roomHeight is the display's clientHeight, padding included: the space the one line is
     * centred in once the padding is gone. tightLineHeight is the number handed to CSS, and the
     * last guard makes sure a nonsense room can never become line-height: 0px or NaN: a box with
     * no room at all keeps the normal line-height instead.
     *
     * The tight decision itself must not depend on that guard, or on anything the tight styling
     * changes. A box too short for one line stays tight whether or not its padding is currently
     * drawn: clientHeight equals the padding once the padding is the only thing left, so a
     * decision that flipped back to roomy on a zero room would restore the padding, measure it,
     * turn tight again, and loop until React stopped it (seen on a page saved without rowHeight,
     * which the PDF renderer draws with 1px rows).
     */
    const normalizedPadding = padding || 0;
    const hasMeasuredBox = Number.isFinite(textBoxHeight);
    const roomHeight = hasMeasuredBox ? textBoxHeight + (2 * normalizedPadding) : null;

    const isTightBox = isPlainWrap
      && !isMeasurementPass
      && hasMeasuredBox
      && lineHeightPixels > textBoxHeight;

    const tightLineHeight = (isTightBox && Number.isFinite(roomHeight) && roomHeight > 0) ? roomHeight : lineHeightPixels;

    /**
     * How far the glyphs of that centred line stick out above and below the room.
     *
     * A tight box is shorter than the font's own inline box (a 12px font draws 14px of glyphs,
     * a 14px font 16px), so centring the line leaves its ascenders and descenders hanging a
     * pixel or two past the padding box. Clipping exactly at the padding box cut the tail of
     * every "g" and "y" in the PDF. The clip is widened by this much instead, so the one line
     * prints whole while a wrapped second line, which starts a full line lower, stays hidden.
     * The normal line-height (font x 1.2) is a safe stand-in for the inline box height.
     */
    const tightClipMargin = isTightBox ? Math.max(0, Math.ceil((lineHeightPixels - tightLineHeight) / 2)) : 0;

    /**
     * The line limit, tightened to what the box can really show.
     *
     * maxLines on its own knows nothing about the height it has to fit into, and the clamp
     * limits lines rather than pixels, so a limit of five in a box with room for two produced
     * a block taller than the field. Field.js then cut it at a pixel, half way through a row
     * of letters.
     *
     * Left untouched in an export, where the field grows to fit its text instead of the text
     * being cut to fit the field. That covers both exporter modes: on a float page the box is
     * about to be grown from the overflow this render exposes, and a clamp would hide the very
     * lines being measured.
     */
    const effectiveMaxLines = (!isAutoShrinking || isExporterExpandable || isExporterAutoHeight) ? autoShrinkMaxLines : getFittingMaxLines({

      /**
       * With auto-height on there is no line limit to enforce: whatever the value needs, the
       * box is about to be made tall enough for. Infinity leaves the box as the only thing
       * tightening the clamp, which is what getFittingMaxLines then works out.
       */
      maxLines: isAutoHeight ? Infinity : autoShrinkMaxLines,

      //Exact once the node has been drawn. The rest are the fallback for the first render
      availableHeight: textBoxHeight,
      designHeight,
      padding,
      borderWidth,
      reservedHeight: titleHeight,
      lineHeightPixels,
    });

    /**
     * A div has no placeholder attribute, so an empty wrapped field would render as a blank
     * box where it previously showed its prompt. getDisplayValue returns '' for null,
     * undefined and empty string alike, so this one check covers every empty case.
     */
    const showPlaceholder = !!placeholder && (displayValue === '' || displayValue === undefined || displayValue === null);

    let fieldNode;

    if (isValueWrapping) {

      /**
       * Every style both nodes need, gathered once so the display and the editor can not
       * drift apart and change appearance as focus moves in and out. This list mirrors what
       * FieldInput is handed in the final branch below; anything omitted is a style that
       * silently stops applying as soon as a field starts wrapping.
       */
      const wrapTypography = {
        fontSize: effectiveFontSize,
        fontColor,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        borderRadius,
        borderWidth,
        borderColor,
        backgroundColor,
        padding,
        primaryColor,
        border,
        disabled,
        textOverflow,

        /**
         * FieldInput uses these three together to decide when text may bleed past the border
         * for PDF export. The display node needs the same information to reach the same
         * decision, or export behaviour changes on float and transparent pages.
         */
        fieldMode,
        pageLayoutMode,
        fieldPresentationMode,
      };

      if (focused && !disabled) {

        /**
         * Editing. A real textarea holding the complete, untruncated value, which is what
         * lets the user keep typing past the visible truncation: the ellipsis was only ever
         * a display effect and the stored value was never touched.
         */
        const editorProps = {
          'data-testid': `${fieldPositionId}-text-field`,

          //A plain wrapping field is focused from setTextareaRef instead, with preventScroll
          autoFocus: !isPlainWrap,
          defaultValue: displayValue != null ? String(displayValue) : '',

          //This branch replaces FieldInput, which was handed the placeholder
          placeholder,

          disabled,

          /**
           * This editor is a real textarea, which is what lets the value wrap and stops a
           * tall field collapsing when it is clicked into. A textarea also accepts Enter,
           * which a one line text field must not: the <input> this replaced could never
           * store a newline, and a stored newline silently switches the field's own
           * auto-shrink off, because shrinking can not undo a line break the user typed.
           *
           * Only reachable for type text. Multiline fields are TextareaField, a separate
           * component, and keep Enter.
           */
          onKeyDown: (e) => {
            if (e.key === 'Enter') e.preventDefault();
          },

          onChange: (e) => {

            const raw = e.target.value;

            /**
             * Enter is blocked above, but pasting is not a key press, so multi line
             * clipboard content still arrives here in one go. Joining with a space rather
             * than deleting keeps the words either side of the break apart, which is what a
             * browser does when you paste multi line text into a plain input.
             *
             * All three line endings are listed rather than using \r?\n, because that
             * pattern misses a lone carriage return, which older clipboard content can
             * still carry. \r\n is listed first so a Windows break is consumed as one unit
             * and becomes one space instead of two.
             */
            const singleLine = raw.replace(/\r\n|\r|\n/g, ' ');

            if (singleLine !== raw) {

              /**
               * This textarea is uncontrolled: it is given defaultValue, not value, so React
               * will not push the cleaned string back into the DOM node. Without writing it
               * back by hand the user keeps seeing their pasted line breaks on screen while
               * the stored value has already had them removed, and auto-shrink then measures
               * something different from what is displayed.
               */
              const caret = e.target.selectionStart;

              /**
               * Where the caret ends up, worked out by cleaning only the text in front of
               * it. A plain "\n" becomes one space so nothing moves, but a Windows "\r\n" is
               * two characters becoming one, which shifts everything after it left. Same
               * pattern as above, so the two can never disagree about a position.
               */
              const nextCaret = raw.slice(0, caret).replace(/\r\n|\r|\n/g, ' ').length;

              e.target.value = singleLine;
              e.target.setSelectionRange(nextCaret, nextCaret);

            }

            this.setState({ changes: getChangeValue(type, singleLine) });

          },
          onFocus: (e) => {
            if (!disabled && onFocus) {
              onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
            }
          },
          onBlur: () => {

            const { changes: nextChanges } = this.state;

            /**
             * Read first, while this textarea is still on screen. A moment later focused is
             * false, the editor has been swapped for the display node, and there is nothing
             * left to measure.
             *
             * Measured on every blur, not only when the value changed, so a field that was
             * already overflowing when it was clicked into still grows. Shrinking is allowed
             * only when this focus changed the value: that is what the request records.
             */
            let measurement = null;

            if (isAutoHeight && !disabled) {

              const request = this.openAutoHeightRequest(nextChanges, value);

              measurement = measureAutoHeight(this.textareaRef.current, nextChanges, type, height, { edited: request.edited });

              if (measurement) measurement.generation = request.generation;

            }

            if (focused) this.setState({ focused: false });
            if (!disabled && value !== nextChanges) onChange(fieldId, fieldIdentifier, fieldPositionId, nextChanges);
            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

            /**
             * Deferred to the next task, and it has to be. The value change above and this
             * height change both read the same document and write it back, so sent in the same
             * tick the second one would be built on the pre-value document and would drop what
             * the user just typed.
             *
             * setTimeout rather than requestAnimationFrame, which the measuring paths in this
             * codebase use: a browser does not run animation frames for a hidden tab, so a
             * growth measured just before the user switched tabs would sit unpersisted until
             * they came back. A zero timeout runs regardless and still lands after React has
             * flushed the value change.
             */
            if (measurement && onAutoHeightChange) {

              /**
               * One pending emission at a time. The browser can deliver two blur events for a
               * single loss of focus (it does for this editor when a click both selects the
               * field and focuses it), and two emissions built from one measurement would grow
               * the box twice. The later blur wins; nothing about the box has changed between
               * them. A retry scheduled by an earlier answer is superseded as well.
               */
              clearTimeout(this.autoHeightTimer);
              clearTimeout(this.autoHeightRetryTimer);
              this.autoHeightRetries = 0;

              this.autoHeightTimer = setTimeout(() => this.emitAutoHeight(measurement), 0);

            }

          },
        };

        /**
         * A dedicated styled textarea rather than StyledInputGroupTextarea, which is built on
         * inputGroupWrapStyles and has no height, so reusing it would let a tall field
         * collapse on click. Defining our own also leaves the input group path untouched.
         */
        fieldNode = (
          <StyledAutoShrinkTextarea
            {...editorProps}
            {...wrapTypography}
            $fitBox={isAutoHeight}
            $noFloor={isExportNoFloor}
            $plain={isPlainWrap}
            $tight={isTightBox}
            $tightLineHeight={tightLineHeight}
            $clipMargin={tightClipMargin}
            $measuring={isMeasurementPass}

            /**
             * Only stores the element. Focus comes from autoFocus in editorProps, and the caret
             * is placed once from componentDidUpdate. Calling focus() here as well would run on
             * every render and could pull focus back after it had moved elsewhere.
             */
            ref={this.setTextareaRef}
          />
        );

      } else {

        /**
         * Displaying. Covers idle edit and fill, readonly, and the PDF exporter, which always
         * lands here because it renders every field disabled. That is what makes the export
         * match what the user saw.
         */
        const displayProps = {
          'data-testid': `${fieldPositionId}-text-field`,

          //Only clamp when auto-shrink is on, and never in an export where every line must print
          maxLines: (isAutoShrinking && !isExporterExpandable && !isExporterAutoHeight) ? effectiveMaxLines : undefined,

          //A plain div is not focusable, so without this wrapped fields leave the tab order
          tabIndex: disabled ? -1 : tabIndex,

          disabled,
          onClick: () => {
            if (!disabled) this.setState({ focused: true });
          },
          onFocus: () => {
            if (!disabled) this.setState({ focused: true });
          },
        };

        /**
         * The text sits in its own element so the outer node can carry the border, background
         * and full drawn height while this one carries the line clamp. Text styles are passed
         * explicitly rather than left to inheritance.
         */
        const displayTextProps = {
          maxLines: (isAutoShrinking && !isExporterExpandable && !isExporterAutoHeight) ? effectiveMaxLines : undefined,
          fontSize: effectiveFontSize,
          fontColor,
          fontStyle,
          fontWeight,
          textAlign,
          textTransform,
          textDecoration,
          isPlaceholder: showPlaceholder,
        };

        /**
         * The placeholder is shown as display text only. It never enters state and never
         * reaches onChange, so it can not be saved as the field value.
         */
        let displayText = '';
        if (showPlaceholder) displayText = placeholder;
        else if (displayValue !== null && displayValue !== undefined) displayText = String(displayValue);

        fieldNode = (
          <StyledAutoShrinkDisplay
            {...displayProps}
            {...wrapTypography}
            $fitBox={isAutoHeight}
            $noFloor={isExportNoFloor}
            $plain={isPlainWrap}
            $tight={isTightBox}
            $clipMargin={tightClipMargin}
            $measuring={isMeasurementPass}
            expandable={isExporterExpandable}
            clamped={isAutoShrinking}
            ref={(e) => {
              //Existing behaviour: keeps the editor from collapsing when clicked into
              if (e) this.wrapDisplayHeight = e.offsetHeight;

              /**
               * The node the exporter measures for overflow and the box watcher follows. Written
               * on null as well, so syncDisplayObserver sees the display go away when the editor
               * replaces it; the editor is never mounted in an export.
               */
              this.displayNode = e;

              /**
               * The room left for text, used to cap the line limit, to size the font and, for a
               * plain wrapping field, to decide the tight box.
               *
               * Read in the exporter as well: the node is pinned to its box in both passes, and
               * a plain field's final pass needs the tight decision.
               *
               * Only written when the number actually changes, so this settles after one
               * extra render instead of looping. It is safe from feedback because the node's
               * height here comes from the grid, not from how many lines it holds, so
               * capping the lines cannot change what was measured, and dropping the vertical
               * padding in a tight box does not change clientHeight either.
               */
              if (e) {
                const nextTextBoxHeight = e.clientHeight - ((padding || 0) * 2);
                if (nextTextBoxHeight !== textBoxHeight) this.setState({ textBoxHeight: nextTextBoxHeight });
              }
            }}
          >
            <StyledAutoShrinkDisplayText
              {...displayTextProps}
              $tight={isTightBox}
              $tightLineHeight={tightLineHeight}
              ref={this.setDisplayTextRef}
            >
              {displayText}
            </StyledAutoShrinkDisplayText>
          </StyledAutoShrinkDisplay>
        );

      }

    } else if (isWrapMode) {

      const wrapTypography = {
        fontSize,
        fontColor,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        borderRadius,
        borderWidth,
        backgroundColor,
        padding,
        primaryColor,
        border,
      };

      if (focused && !disabled) {
        fieldNode = (
          <StyledInputGroupTextarea
            data-testid={`${fieldPositionId}-text-field`}
            autoFocus
            ref={(e) => {
              this.textareaRef.current = e;
              if (!e) return;
              e.focus();
            }}
            defaultValue={displayValue != null ? String(displayValue) : ''}
            style={this.wrapDisplayHeight ? { height: `${this.wrapDisplayHeight}px`, minHeight: `${this.wrapDisplayHeight}px` } : undefined}
            disabled={disabled}
            {...wrapTypography}
            onChange={(e) => {
              this.setState({ changes: getChangeValue(type, e.target.value) });
            }}
            onFocus={(e) => {
              if (!disabled && onFocus) {
                onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
              }
            }}
            onBlur={() => {
              const nextChanges = this.state.changes;
              if (focused) this.setState({ focused: false });
              if (!disabled && value !== nextChanges) onChange(fieldId, fieldIdentifier, fieldPositionId, nextChanges);
              if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
              //Formula cells only: the prop is the source of truth once the edit is dispatched (see displayValue)
              if (hasDisplayValue) this.setState({ changes: value });
            }}
          />
        );
      } else {
        fieldNode = (
          <StyledInputGroupDisplay
            data-testid={`${fieldPositionId}-text-field`}
            data-ig-wrap-display="true"
            ref={(e) => { if (e) this.wrapDisplayHeight = e.offsetHeight; }}
            disabled={disabled}
            {...wrapTypography}
            onClick={() => {
              if (!disabled) {
                this.setState({ focused: true });
              }
            }}
          >
            {blurredValue != null ? String(blurredValue) : ''}
          </StyledInputGroupDisplay>
        );
      }

    } else {

      fieldNode = (
        <FieldInput
          data-testid={`${fieldPositionId}-text-field`}
          tabIndex={tabIndex}
          value={focused ? displayValue : blurredValue}
          type={getFieldInputType(disabled, type)}
          placeholder={placeholder}
          {...{
            disabled,
            focused,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            primaryColor,
            fieldId,
            fieldMode,
            pageLayoutMode,
            fieldPresentationMode,
          }}
          onChange={(e) => {
            this.setState({ changes: getChangeValue(type, e.target.value) });
          }}
          onFocus={(e) => {

            if (!focused) this.setState({ focused: true });

            if (!disabled && onFocus && !focused) {
              onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
            }

          }}
          onBlur={(e) => {

            if (focused) this.setState({ focused: false });

            if (!disabled && value !== changes) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

            //Formula cells only: the prop is the source of truth once the edit is dispatched (see displayValue)
            if (hasDisplayValue) this.setState({ changes: value });

          }}
        />
      );

    }

    return (
      <StyledWrapper
        {...{ margin }}
        $clipOverflow={isTightBox && !disabled && fieldMode !== FieldModes.pdf}
        style={wrapperStyles || {}}

        /**
         * The node the exporter measures. It has to be this one rather than the display node
         * inside it, because the height the exporter needs is the whole field: the title row
         * and the wrapper's own padding as well as the text. Measuring the inner node
         * under-reported by the height of the title, so the grown box came out too short and
         * the text overflowed it again. TextareaField measures its outer wrapper for the
         * same reason.
         *
         * Stored once. Storing sets state, setting state renders again, and an unguarded
         * store would therefore never settle.
         */
        ref={(e) => { if (e && !wrapperRef) this.setState({ wrapperRef: e }); }}
      >
        <StyledInternalWrapper>
          <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
            <FieldTitle
              {...{
                title,
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleTextAlign,
                titleFontStyle,
                titleFontWeight,
                titleTextTransform,
                titleTextDecoration,
                titleTextOverflow,
              }}
            />
            <FieldDecorators
              decorators={decorators}
              fieldMode={fieldMode}
              fieldId={fieldId}
              fieldIdentifier={fieldIdentifier}
              fieldPositionId={fieldPositionId}
              onDecoratorClick={onDecoratorClick}
            />
          </StyledFieldHeader>
          {getTooltip()}
          {fieldNode}
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }
}

export const getFieldInputType = (disabled, type) => {
  return !disabled ? type : 'text';
};

export const getDisplayValue = (type, value) => {

  /**
   * <----------------------------NOTICE------------------------->
   * Text field component and functions inside this file are used in
   * many places.
   *
   * 0 is technically considered as a falsy value (!value) but, we have
   * value!==0 here so we could display 0 as the default value for xMin
   * and ymin in the chartFieldForm.
   *
   */
  if (!value && value !== 0) {
    return '';
  }
  else if (type === 'number' && !Number.isNaN(value)) {
    return value;
  }
  else if (value) {
    return value;
  }

};

export const getChangeValue = (type, value) => {

  let nextValue = value;

  if (type === 'number' && typeof nextValue === 'string' && nextValue !== '') {
    /**
     * This was to prevent the number from being saved as a string on the db
     */
    nextValue = nextValue.indexOf('.') === -1 ? parseInt(nextValue, 10) : parseFloat(nextValue, 10);

  }

  return nextValue;

};

/**
 * The read-only node shown when nobody is typing. A div, not an input, because only a div can
 * render more than one line.
 *
 * <----------------------------NOTICE------------------------->
 * inputGroupWrapStyles has min-height but no height, because it was written for input group
 * cells that size to their content. FieldInput, the node this replaces, sets height: 100% so
 * its border and background always fill the rectangle the designer drew. Borrowing the shared
 * block alone would collapse a tall field down to its text and shrink the visible border,
 * background and click target with it, so the height is restored explicitly below.
 *
 * This is also deliberately not a flex container. Centring wrapped text makes it overflow
 * upward as well as downward, which would clip the start of a sentence in fill mode and bleed
 * over the field above in the export. FieldInput's StyledSpan, the existing equivalent, is a
 * plain span whose text starts at the top and bleeds only downward.
 */
const StyledAutoShrinkDisplay = styled.div`
  ${(props) => inputGroupWrapStyles(props)}

  /*
   * Fills the rectangle the designer drew, matching FieldInput, and claims the space left under
   * the title row as a child of StyledInternalWrapper. Pinned in the exporter's measuring pass
   * too: the measurement reads the text span inside this node and compares it with this box
   * (handleExportMeasurement), so this node must never grow to its contents.
   */
  height: 100%;
  flex: 1;

  /*
   * The 25px floor, kept for auto-shrink only.
   *
   * It came from inputGroupWrapStyles, written for input group cells that size to their content
   * and need a floor so an empty cell stays clickable. A standalone field is pinned to the box
   * the grid drew (height: 100%; flex: 1 above), so it can never collapse, and on the short boxes
   * PDF forms are full of the floor only pushed this node out of its cell: the reader sees 20px
   * while the node reports 25. A plain wrapping field ($plain) therefore drops it, and auto-height
   * ($fitBox) already did, because its overflow is measured from this node and a floored node
   * would report that a value fits when it does not. Auto-shrink keeps it, and with it the exact
   * room its font search and line clamp see today.
   *
   * Off for every field in the exporter's measuring pass ($measuring), so the room
   * handleExportMeasurement reads is the drawn box and not the floored node. The measurement
   * floors its own need at the same number, so the final render, floor back on, always fits.
   */
  min-height: ${(props) => (
    props.$plain || props.$fitBox || props.$measuring || props.$noFloor
      ? 0
      : AUTO_SHRINK_MIN_HEIGHT
  )}px;

  /*
   * Tight box (plain wrap only, see render): one line does not fit inside the vertical padding,
   * so the padding goes and the line is centred by its line-height instead, the way rc25's
   * <input> drew it. Horizontal padding stays, so the text does not shift sideways.
   */
  ${(props) => (props.$tight ? 'padding-top: 0; padding-bottom: 0;' : '')}

  /* The whole drawn rectangle is clickable, not just the line of text */
  cursor: pointer;

  /* Text starts at the top and grows downward. See the notice above. */
  display: block;

  /*
   * Keep the text inside this element's own border.
   *
   * Without this the text escapes the field entirely and is cut by Field.js's overflow rule
   * on the outer wrapper, which is what produced the row of half letters at the border.
   *
   * With auto-shrink on: hidden, so the clamped block is contained. This matches the
   * multiline auto-shrink display node, which already sets exactly this.
   *
   * With auto-shrink off: there is no clamp at all, so the value scrolls instead of being
   * clipped and nothing is lost. That is what a multiline field does in edit mode today,
   * where textareaStyles resolves overflow-y to auto.
   *
   * While the exporter is growing the field: nothing, because there the box is being sized
   * to the text rather than the text cut to the box.
   *
   * Two plain-wrap rules come first. A tight box clips: a 12px box can never show a usable
   * scrollbar. The clip is the padding box widened by $clipMargin (overflow: clip with
   * overflow-clip-margin; hidden is the fallback for a browser without clip), because the
   * centred line's glyphs overhang a box that is shorter than the font's inline box, and a clip
   * at the padding box cut every descender. And a plain field in pdf mode
   * hides it whatever else is true: a PDF must never show a scrollbar or let text spill into the
   * field below. That rule sits above the exporter's "no overflow rule" on purpose: header and
   * footer fields are marked expandable by FieldRenderer but are never measured or grown, so
   * left to the old order a long header value bled into the page. For a measured grid field the
   * order is harmless: its text span still reports its full height while measuring, and the
   * final box has already been grown to fit.
   */
  ${(props) => {
    if (props.$tight) return `overflow: hidden; overflow: clip; overflow-clip-margin: ${props.$clipMargin || 0}px;`;
    if (props.$plain && props.fieldMode === FieldModes.pdf) return 'overflow: hidden;';
    if (props.expandable) return '';
    return props.clamped ? 'overflow: hidden;' : 'overflow-y: auto; overflow-x: hidden;';
  }}

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

/**
 * The editor, shown while the field is being typed in. It must occupy the same rectangle as
 * the display node above, or a tall field visibly changes size the moment it is clicked.
 */
const StyledAutoShrinkTextarea = styled.textarea`
  ${(props) => inputGroupWrapStyles(props)}

  /* The same box filling rules as the display node, so the two are interchangeable in place */
  height: 100%;
  flex: 1;

  /* The same floor rule as the display node: auto-shrink keeps 25px, plain wrap and auto-height do not, off while measuring */
  min-height: ${(props) => (
    props.$plain || props.$fitBox || props.$measuring || props.$noFloor
      ? 0
      : AUTO_SHRINK_MIN_HEIGHT
  )}px;

  /*
   * Explicit, and it has to match StyledAutoShrinkDisplayText exactly. Left alone this textarea
   * inherits line-height: 1 from GridLayout's StyledIMPORTANT, so the text is drawn 20% tighter
   * while it is being typed than it is once the field is idle, and a height measured from it
   * would describe a box the reader never sees.
   *
   * !important for the same reason inputGroupWrapStyles needs it on font-size: StyledIMPORTANT
   * sets line-height: inherit on every textarea beneath it with a class-plus-element selector,
   * which outranks this component's own class. Without it the rule is silently lost.
   */
  line-height: ${(props) => (props.$tight ? props.$tightLineHeight : getLineHeightPixels(props.fontSize))}px !important;

  /*
   * Text longer than the box scrolls inside it rather than resizing it.
   *
   * A tight box (plain wrap only) uses the same rule as the display: no vertical padding, no
   * scrollbar, and the line-height above set to the room so the caret line sits exactly where
   * the idle line was drawn and nothing moves on click.
   */
  ${(props) => (props.$tight
    ? `padding-top: 0; padding-bottom: 0; overflow: hidden; overflow: clip; overflow-clip-margin: ${props.$clipMargin || 0}px;`
    : 'overflow-y: auto;')}

  /* Every other textarea in this codebase disables the browser's resize handle */
  resize: none;

  &::placeholder {
    color: ${InputPlaceholderGrey};
  }

  &:focus, &:focus-visible {
    outline: unset;
    border: ${(props) => props.borderWidth}px solid ${(props) => props.primaryColor};
  }
`;

/**
 * The text itself lives in its own element, because -webkit-line-clamp only applies to an
 * element that is a -webkit-box, and the outer node has to stay an ordinary block so it can
 * carry the border, background and full drawn height.
 */
const StyledAutoShrinkDisplayText = styled.span`
  width: 100%;

  /* Placeholder text is greyed the way a browser greys a native one */
  color: ${(props) => (props.isPlaceholder ? InputPlaceholderGrey : props.fontColor)};

  font-style: ${(props) => props.fontStyle};
  font-weight: ${(props) => props.fontWeight};
  text-align: ${(props) => props.textAlign};
  text-transform: ${(props) => props.textTransform};
  text-decoration: ${(props) => props.textDecoration};

  /**
   * The clamp when a line limit was asked for, ordinary wrapping when it was not.
   *
   * display: block is spelled out in the non clamped branch because a span is inline by
   * default, and width: 100% above is ignored on an inline element, which would leave
   * text-align with nothing to align within so centre and right would fall back to left. The
   * clamped branch does not need it, since the clamp styles already set display: -webkit-box.
   */
  ${(props) => (props.maxLines ? getAutoShrinkClampStyles({ maxLines: props.maxLines }) : `
    display: block;
    white-space: pre-line;
    overflow-wrap: break-word;
  `)}

  /*
   * Explicit, so the clamp lines up with the box rather than following a browser default.
   *
   * In a tight box (plain wrap only) the line-height is the room itself. A line-height smaller
   * than the font centres the glyphs on the line with negative half-leading, which is exactly
   * where rc25's <input> drew its one line, and the editor uses the same number so nothing
   * moves on click. This one value is the whole centring mechanism.
   */
  line-height: ${(props) => (props.$tight ? props.$tightLineHeight : getLineHeightPixels(props.fontSize))}px;
`;

export default TextField;
